import { Ref, computed, inject, onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import {
  EntityType,
  KpiSettings,
  KpiType,
  KpiResult,
  KpiQuery
} from '@/api-client/models/utilization/Contracts'
import { useFetchKpi } from '@/composables/useFetchKpi'
import { KpiPerRoomGroup, calculateKpi, calculateKpiPerRoomGroup } from '../calculateKpi'
import { defineStore } from 'pinia'
import _ from 'lodash'
import { useFeature } from '@/pinia/features'
import { useStore } from 'vuex'
import { sharedDataKey } from '../../types'
import { CoreType, FilterQueryDefault } from '@/api-client/models/shared/Contracts'
import { FeatureType, useCurrentFeatureFilters, useFilters } from '@/composables/useFilters'
import { calculateDefaultFilterQuery, calculateEntityType } from '@/fetches'

export function useKpiQueries() {
  const { feature } = useFeature('home/analytics/analyticareas/usage/kpis')
  const kpiSettings = computed(() => feature.value?.props?.kpis)
  const filters = useFilters(FeatureType.Utilization)
  const { submitted } = toRefs(filters)
  const activeFilter = computed(() => calculateDefaultFilterQuery(submitted.value))

  const groupedSettings = computed(() => {
    if (!kpiSettings.value) return []

    return _(kpiSettings.value)
      .groupBy((setting) => hash(setting.filterOverride))
      .values()
      .value()
  })

  const kpiQueries = computed<KpiQuery[] | undefined>(() => {
    const filter = activeFilter.value
    if (!groupedSettings.value || !filter) {
      return []
    } else {
      return groupedSettings.value.map((group) => createQuerySetting(group, filter))
    }
  })

  return {
    kpiSettings,
    kpiQueries
  }
}

export function useKpisFromQuery(query: Ref<KpiQuery | null>) {
  const { data, isLoading, cancel } = useFetchKpi(query)
  const setKpi = useKpis().setKpi

  const kpiValues = computed(() => data.value?.kpis || [])

  const kpiValueMap = computed<Record<KpiType, KpiResult> | undefined>(() =>
    kpiValues.value?.reduce(
      (map, kpi) => ({ ...map, [kpi.kpi]: kpi }),
      {} as Record<KpiType, KpiResult>
    )
  )

  const numericKpiValueMap = computed<Record<KpiType, number | undefined>>(
    () =>
      Object.fromEntries(
        kpiValues.value?.map((kpi) => [kpi.kpi, kpi.summary.value]) || []
      ) as Record<KpiType, number | undefined>
  )

  const numericKpiPerRoomGroupMap = computed<Record<KpiType, KpiPerRoomGroup[] | undefined>>(
    () =>
      Object.fromEntries(
        kpiValues.value?.map((kpi) => [kpi.kpi, calculateKpiPerRoomGroup(kpi)]) || []
      ) as Record<KpiType, KpiPerRoomGroup[] | undefined>
  )
  const missingData = ref(false)

  watch(data, () => {
    if (data.value) {
      const allValuesAreZero = data.value.kpis.every((kpi) => kpi.summary.value === 0)
      if (allValuesAreZero && data.value.kpis.length === 2) {
        missingData.value = true
      } else {
        missingData.value = false
        data.value.kpis.forEach((kpi) => {
          setKpi(kpi)
        })
      }
    }
  })

  onUnmounted(() => {
    cancel()
  })

  return {
    kpiValues,
    kpiValueMap,
    numericKpiValueMap,
    numericKpiPerRoomGroupMap,
    isLoading,
    missingData
  }
}

export const useKpis = defineStore('kpis', () => {
  const { feature } = useFeature('home/analytics/analyticareas/usage/kpis')
  const kpiSettings = computed(() => feature.value?.props?.kpis)
  const selectedKpiType = ref<KpiType>()
  const filters = useCurrentFeatureFilters()
  const sharedData = inject(sharedDataKey)
  const store = useStore()

  const kpiSettingsMap = computed<Record<KpiType, KpiSettings | undefined> | undefined>(
    () =>
      kpiSettings.value?.reduce(
        (map, kpi) => ({ ...map, [kpi.kpi]: kpi }),
        {} as Record<KpiType, KpiSettings>
      ) ?? ({} as Record<KpiType, KpiSettings>)
  )

  const mapFilterLevel = computed(() => calculateEntityType(filters.value?.submitted))

  const isShowOnMapEnabled = (kpi: KpiSettings): boolean =>
    mapFilterLevel.value !== undefined &&
    kpi.showOnMapEnabledFor.indexOf(mapFilterLevel.value) !== -1

  const currentKpiSettings = computed<KpiSettings | undefined>(() =>
    selectedKpiType.value ? kpiSettingsMap.value?.[selectedKpiType.value] : undefined
  )

  const kpis = ref<Record<KpiType, KpiResult>>({} as Record<KpiType, KpiResult>)

  const selectedKpi = computed<KpiResult | undefined>(() =>
    selectedKpiType.value ? kpis.value?.[selectedKpiType.value] : undefined
  )
  const selectedNumericValue = computed<number | undefined>(() =>
    selectedKpiType.value ? calculateKpi(selectedKpi.value) : undefined
  )

  function setKpiSelection(kpiType: KpiType | undefined) {
    selectedKpiType.value = kpiType
  }

  function toggleKpiSelection(kpiType: KpiType) {
    if (selectedKpiType.value === kpiType) {
      selectedKpiType.value = undefined
    } else {
      selectedKpiType.value = kpiType
    }
  }
  function setKpi(kpi: KpiResult) {
    kpis.value = {
      ...kpis.value,
      [kpi.kpi]: kpi
    }
  }

  const showOnMapEnabled = computed(
    () =>
      (store.getters.isPlanviewShowing ||
        store.state.clickedItemType === 'complex' ||
        store.state.clickedItemType === 'realEstate' ||
        store.state.clickedItemType === 'building') &&
      !sharedData?.fullscreen
  )

  const firstKpi = computed<KpiType | undefined>(() => kpiSettings.value?.[0]?.kpi)

  watch(
    showOnMapEnabled,
    (enabled) => {
      if (!enabled) {
        setKpiSelection(undefined)
      } else {
        setKpiSelection(firstKpi.value)
      }
    },
    { immediate: true }
  )

  watch(
    firstKpi,
    (newFirst, oldFirst) => {
      if (newFirst && !oldFirst && showOnMapEnabled.value) {
        setKpiSelection(newFirst)
      }
    },
    { immediate: true }
  )

  onMounted(() => {
    sharedData?.updateSectionIndex(0)
    if (showOnMapEnabled.value) {
      setKpiSelection(firstKpi.value)
    }
  })

  return {
    toggleKpiSelection,
    isShowOnMapEnabled,
    setKpiSelection,
    setKpi,
    selectedKpi,
    selectedNumericValue,
    selectedKpiType,
    currentKpiSettings,
    kpiSettingsMap,
    kpiSettings,
    showOnMapEnabled
  }
})

function createQuerySetting(group: KpiSettings[], filter: FilterQueryDefault): KpiQuery {
  const combinedFilter = {
    ...filter,
    ..._.omitBy(group[0].filterOverride, _.isNil)
  }

  return {
    kpis: group.map((setting) => ({
      kpi: setting.kpi,
      splitBy: getKpiSplitBy(filter.entityType),
      parameters: setting.parameters
    })),
    filter: combinedFilter
  }
}

function getKpiSplitBy(entity: CoreType): EntityType {
  switch (entity) {
    case 'COMPLEX':
      return 'CONSTRUCTION'
    case 'CONSTRUCTION':
      return 'LEVEL'
    case 'LEVEL':
      return 'SPACE'
    case 'SPACE':
      return 'SPACE'
  }
  return 'SPACE' // Default case if no match found
}

function hash(object: any | undefined) {
  return Buffer.from(
    JSON.stringify({
      keys: Object.keys(object || {}).sort(),
      values: Object.values(object || {}).sort()
    })
  ).toString('base64')
}
